import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { RentalListing } from '../types';
import rentalApi from '../services/api';
import './PinPopup.css';
import './ListingPopup.css';

interface ListingPopupProps {
  listing: RentalListing;
  onClose: () => void;
}

type View = 'detail' | 'interested' | 'report';

const REPORT_REASONS = [
  { value: 'IncorrectInfo',        label: 'Incorrect info / price' },
  { value: 'Scam',                 label: 'Looks like a scam' },
  { value: 'InappropriateContent', label: 'Inappropriate content' },
  { value: 'AlreadyRented',        label: 'Already rented' },
  { value: 'Other',                label: 'Other' },
];

const CloseIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const ShareIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </svg>
);
const FlagIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" /><line x1="4" y1="22" x2="4" y2="15" />
  </svg>
);
const WhatsAppIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);
const LinkIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

export const ListingPopup: React.FC<ListingPopupProps> = ({ listing, onClose }) => {
  const [view, setView] = useState<View>('detail');
  const [shareOpen, setShareOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  // Interested form state
  const [iName, setIName]       = useState('');
  const [iEmail, setIEmail]     = useState('');
  const [iPhone, setIPhone]     = useState('');
  const [iMessage, setIMessage] = useState('');
  const [iLoading, setILoading] = useState(false);
  const [iDone, setIDone]       = useState(false);
  const [iError, setIError]     = useState('');

  // Report form state
  const [rReason, setRReason]   = useState('');
  const [rDesc, setRDesc]       = useState('');
  const [rEmail, setREmail]     = useState('');
  const [rLoading, setRLoading] = useState(false);
  const [rDone, setRDone]       = useState(false);
  const [rError, setRError]     = useState('');

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const isRoom    = listing.listing_type === 'RentingRoom';
  const typeLabel = isRoom ? 'Room Available' : 'Whole Unit Available';
  const postedDate = new Date(listing.created_at).toLocaleDateString('en-CA', {
    year: 'numeric', month: 'short', day: 'numeric',
  });

  const handleShareWhatsApp = () => {
    const text = `🏠 ${typeLabel} in ${listing.city}: $${listing.budget.toLocaleString()}/mo${listing.bhk ? ` · ${listing.bhk}BHK` : ''}\n📍 ${listing.address}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    setShareOpen(false);
  };

  const handleCopyLink = async () => {
    const url = `${window.location.origin}${window.location.pathname}?listing=${listing.id}`;
    try { await navigator.clipboard.writeText(url); } catch { /* ignore */ }
    setLinkCopied(true);
    setShareOpen(false);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const handleInterestSubmit = async () => {
    setIError('');
    if (!iName.trim()) return setIError('Name is required');
    if (!iEmail.trim()) return setIError('Email is required');
    setILoading(true);
    try {
      await rentalApi.expressListingInterest(listing.id, {
        name: iName.trim(),
        email: iEmail.trim(),
        phone: iPhone.trim() || undefined,
        message: iMessage.trim() || undefined,
      });
      setIDone(true);
    } catch (err: any) {
      setIError(err.message || 'Failed to submit. Please try again.');
    } finally {
      setILoading(false);
    }
  };

  const handleReportSubmit = async () => {
    setRError('');
    if (!rReason) return setRError('Please select a reason');
    setRLoading(true);
    try {
      await rentalApi.reportListing(listing.id, {
        reason: rReason,
        description: rDesc.trim() || undefined,
        reporterEmail: rEmail.trim() || undefined,
      });
      setRDone(true);
    } catch (err: any) {
      setRError(err.message || 'Failed to submit report.');
    } finally {
      setRLoading(false);
    }
  };

  type Tag = { emoji: string; text: string };
  const unitTags: Tag[] = [
    ...(listing.bhk ? [{ emoji: '🛏', text: `${listing.bhk} BHK` }] : []),
    ...(listing.bath_type ? [{ emoji: '🚿', text: `${listing.bath_type} Bath` }] : []),
    ...(listing.property_type ? [{ emoji: '🏠', text: listing.property_type }] : []),
    ...(listing.furnishing ? [{ emoji: '🪑', text: listing.furnishing }] : []),
    ...(listing.availability ? [{ emoji: '📅', text: listing.availability }] : []),
    ...(listing.pets_allowed_status === 'Yes' ? [{ emoji: '🐾', text: 'Pets OK' }] : []),
    ...(listing.transit_distance ? [{ emoji: '🚌', text: listing.transit_distance }] : []),
    ...(listing.school_rating ? [{ emoji: '🎓', text: `School ${listing.school_rating}/10` }] : []),
  ];

  const flatmateTags: Tag[] = [
    ...(listing.you_are ? [{ emoji: '👤', text: `I am ${listing.you_are}` }] : []),
    ...(listing.comfortable_with ? [{ emoji: '🤝', text: `OK with ${listing.comfortable_with}` }] : []),
    ...(listing.employment_status ? [{ emoji: '💼', text: listing.employment_status }] : []),
    ...(listing.food_preference ? [{ emoji: '🍽', text: listing.food_preference }] : []),
    ...(listing.smoking_habits ? [{ emoji: '🚬', text: listing.smoking_habits }] : []),
  ];

  const popup = (
    <div className="pp-backdrop" onClick={onClose}>
      <div className="pp-card lp-card" onClick={e => e.stopPropagation()}>

        {/* ── Header (always visible) ── */}
        <div className="pp-header">
          <div className="lp-header-left">
            <span className="lp-type-badge">{typeLabel}</span>
            <div className="pp-rent">
              <span className="pp-rent-amount lp-rent-amount">${listing.budget.toLocaleString()}</span>
              <span className="pp-rent-period">/mo</span>
            </div>
          </div>
          <div className="pp-header-actions">
            {view === 'detail' && (
              <>
                {/* Share */}
                <div className="pp-share-wrapper">
                  <button className="pp-action-btn" onClick={() => setShareOpen(s => !s)} title="Share" aria-label="Share">
                    <ShareIcon />
                  </button>
                  {shareOpen && (
                    <div className="pp-share-menu">
                      <button className="pp-share-item pp-share-whatsapp" onClick={handleShareWhatsApp}>
                        <WhatsAppIcon /> WhatsApp
                      </button>
                      <button className="pp-share-item" onClick={handleCopyLink}>
                        <LinkIcon /> {linkCopied ? '✓ Copied!' : 'Copy Link'}
                      </button>
                    </div>
                  )}
                </div>
                {/* Report */}
                <button className="pp-action-btn pp-action-flag" onClick={() => setView('report')} title="Report listing" aria-label="Report">
                  <FlagIcon />
                </button>
              </>
            )}
            {view !== 'detail' && (
              <button className="pp-action-btn" onClick={() => setView('detail')} title="Back" aria-label="Back">
                ←
              </button>
            )}
            <button className="pp-action-btn pp-action-close" onClick={onClose} title="Close" aria-label="Close">
              <CloseIcon />
            </button>
          </div>
        </div>

        {/* ── Detail view ── */}
        {view === 'detail' && (
          <>
            <div className="pp-address">📍 {listing.address}{listing.city ? `, ${listing.city}` : ''}</div>

            {unitTags.length > 0 && (
              <div className="pp-tags">
                {unitTags.map((tag, i) => (
                  <span key={i} className="pp-tag">
                    <span className="pp-tag-emoji">{tag.emoji}</span>
                    <span>{tag.text}</span>
                  </span>
                ))}
              </div>
            )}

            {(listing.utilities_included?.length || listing.amenities?.length) ? (
              <div className="lp-extras">
                {listing.utilities_included?.length ? (
                  <div className="lp-extra-row">
                    <span className="lp-extra-label">Utilities</span>
                    <span className="lp-extra-chips">{listing.utilities_included.join(' · ')}</span>
                  </div>
                ) : null}
                {listing.amenities?.length ? (
                  <div className="lp-extra-row">
                    <span className="lp-extra-label">Amenities</span>
                    <span className="lp-extra-chips">{listing.amenities.join(' · ')}</span>
                  </div>
                ) : null}
              </div>
            ) : null}

            {flatmateTags.length > 0 && (
              <>
                <div className="lp-section-label">About the poster</div>
                <div className="pp-tags lp-flatmate-tags">
                  {flatmateTags.map((tag, i) => (
                    <span key={i} className="pp-tag lp-tag-green">
                      <span className="pp-tag-emoji">{tag.emoji}</span>
                      <span>{tag.text}</span>
                    </span>
                  ))}
                </div>
              </>
            )}

            {listing.description && <p className="pp-description">{listing.description}</p>}

            {listing.lifestyle && (
              <div className="lp-lifestyle">
                <span className="lp-section-label lp-section-label-inline">Lifestyle</span>
                <p className="lp-lifestyle-text">{listing.lifestyle}</p>
              </div>
            )}

            {/* Interested CTA */}
            <button className="lp-interested-cta" onClick={() => setView('interested')}>
              <span className="lp-interested-cta-icon">🙋</span>
              <div className="lp-interested-cta-text">
                <div className="lp-interested-cta-title">I'm Interested</div>
                <div className="lp-interested-cta-sub">Let the poster know — they'll get in touch with you.</div>
              </div>
              <span className="lp-interested-cta-arrow">→</span>
            </button>

            <div className="pp-footer lp-footer">
              <div className="pp-date">📅 Listed {postedDate}</div>
            </div>
          </>
        )}

        {/* ── Interested form ── */}
        {view === 'interested' && (
          <div className="lp-form-view">
            {iDone ? (
              <div className="lp-success">
                <div className="lp-success-icon">✓</div>
                <div className="lp-success-title">Interest sent!</div>
                <div className="lp-success-sub">The poster will reach out to you soon.</div>
              </div>
            ) : (
              <>
                <p className="lp-form-intro">Leave your details and the room poster will contact you directly.</p>
                {iError && <div className="lp-form-error">{iError}</div>}
                <div className="lp-field">
                  <label className="lp-field-label">Name <span className="lp-req">*</span></label>
                  <input className="lp-input" value={iName} onChange={e => setIName(e.target.value)} placeholder="Your name" />
                </div>
                <div className="lp-field">
                  <label className="lp-field-label">Email <span className="lp-req">*</span></label>
                  <input className="lp-input" type="email" value={iEmail} onChange={e => setIEmail(e.target.value)} placeholder="your@email.com" />
                </div>
                <div className="lp-field">
                  <label className="lp-field-label">Phone <span className="lp-opt">optional</span></label>
                  <input className="lp-input" type="tel" value={iPhone} onChange={e => setIPhone(e.target.value)} placeholder="+1 647…" />
                </div>
                <div className="lp-field">
                  <label className="lp-field-label">Message <span className="lp-opt">optional</span></label>
                  <textarea className="lp-input lp-textarea" rows={3} value={iMessage}
                    onChange={e => setIMessage(e.target.value.slice(0, 500))}
                    placeholder="Hi, I'm interested in your listing…" />
                  <span className="lp-char-count">{iMessage.length}/500</span>
                </div>
                <button className="lp-submit-btn" onClick={handleInterestSubmit} disabled={iLoading}>
                  {iLoading ? 'Sending…' : '🙋 Send Interest'}
                </button>
              </>
            )}
          </div>
        )}

        {/* ── Report form ── */}
        {view === 'report' && (
          <div className="lp-form-view">
            {rDone ? (
              <div className="lp-success">
                <div className="lp-success-icon lp-success-icon-flag">⚑</div>
                <div className="lp-success-title">Report submitted</div>
                <div className="lp-success-sub">Thank you — our team will review this listing.</div>
              </div>
            ) : (
              <>
                <p className="lp-form-intro">Help keep listings accurate for everyone.</p>
                {rError && <div className="lp-form-error">{rError}</div>}
                <div className="lp-field">
                  <label className="lp-field-label">Reason <span className="lp-req">*</span></label>
                  <div className="lp-reason-group">
                    {REPORT_REASONS.map(r => (
                      <button key={r.value} type="button"
                        className={`lp-reason-pill${rReason === r.value ? ' on' : ''}`}
                        onClick={() => setRReason(r.value)}>
                        {r.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="lp-field">
                  <label className="lp-field-label">Details <span className="lp-opt">optional</span></label>
                  <textarea className="lp-input lp-textarea" rows={3} value={rDesc}
                    onChange={e => setRDesc(e.target.value.slice(0, 1000))}
                    placeholder="Any additional context…" />
                </div>
                <div className="lp-field">
                  <label className="lp-field-label">Your email <span className="lp-opt">optional</span></label>
                  <input className="lp-input" type="email" value={rEmail} onChange={e => setREmail(e.target.value)} placeholder="for follow-up only" />
                </div>
                <button className="lp-submit-btn lp-submit-report" onClick={handleReportSubmit} disabled={rLoading}>
                  {rLoading ? 'Submitting…' : '⚑ Submit Report'}
                </button>
              </>
            )}
          </div>
        )}

      </div>
    </div>
  );

  return ReactDOM.createPortal(popup, document.body);
};
